import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { useQuery } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { getLotteryDetails, getNomineeApplications } from '@/api/Lottery';
import { Layout } from '@/components/Layout';
import { LayoutCard } from '@/components/LayoutCard';
import AccentTitle from '@/components/UI/AccentTitle';
import { getColumns } from './columns';

// Utility to get nested value by path (e.g., 'additionalInfo.secondaryInfo.email')
function getValueByPath(obj: any, path: string) {
  return path.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
}

interface NomineeApplication {
  id: string;
  name: string;
  email: string;
  contactInfo: string;
  address: string;
  additionalInfo: {
    secondaryInfo: {
      email: string;
      contactInfo: string;
    };
  };
  createdAt: string;
  status?: string;
}

const NomineeApplications: React.FC = () => {
  const { lotteryId } = useParams<{ lotteryId: string }>();
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { data: lotteryData, isLoading: lotteryLoading } = useQuery({
    queryKey: ['lottery', lotteryId],
    queryFn: () => {
      return getLotteryDetails(lotteryId || '');
    },
    enabled: !!lotteryId,
  });

  const { data: applicationsData, isLoading: applicationsLoading } = useQuery({
    queryKey: ['nominee-applications', lotteryId],
    queryFn: () => {
      return getNomineeApplications(lotteryId || '');
    },
    enabled: !!lotteryId,
  });

  const lottery = lotteryData?.data?.data;
  const allApplications: NomineeApplication[] = applicationsData?.data?.data || [];

  // Filter applications based on status
  const applications = useMemo(() => {
    if (statusFilter === 'all') {
      return allApplications;
    }

    return allApplications.filter((app) => {
      const appStatus = app.status?.toLowerCase() || 'pending';

      if (statusFilter === 'not-selected') {
        // "Not selected" includes pending status or no status
        return appStatus === 'pending' || !app.status;
      }

      if (statusFilter === 'selected') {
        // "Selected" includes approved status
        return appStatus === 'approved';
      }

      return appStatus === statusFilter.toLowerCase();
    });
  }, [allApplications, statusFilter]);

  const columns = useMemo(() => getColumns(), []);

  if (lotteryLoading || applicationsLoading) {
    return (
      <Layout>
        <LayoutCard>
          <div className='p-4'>
            <div className='flex items-center justify-center h-64'>
              <div className='text-center'>
                <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primaryColor-normal mx-auto mb-4' />
                <p className='text-neutralColor-medium'>Loading nominee applications...</p>
              </div>
            </div>
          </div>
        </LayoutCard>
      </Layout>
    );
  }

  if (!lottery) {
    return (
      <Layout>
        <LayoutCard>
          <div className='p-4'>
            <div className='text-center'>
              <p className='text-red-500'>Lottery not found.</p>
            </div>
          </div>
        </LayoutCard>
      </Layout>
    );
  }

  return (
    <Layout>
      <LayoutCard>
        <div className='flex justify-between items-center p-4'>
          <AccentTitle title={`Applications - ${lottery.name}`} />
          <div className='flex items-center gap-4'>
            <div className='flex items-center gap-2'>
              <label htmlFor='status-filter' className='text-sm font-medium text-gray-700'>
                Filter by Status:
              </label>
              <select
                id='status-filter'
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className='px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              >
                <option value='all'>All Applications</option>
                <option value='not-selected'>Not Selected</option>
                <option value='selected'>Selected</option>
                <option value='approved'>Approved</option>
                <option value='rejected'>Rejected</option>
              </select>
            </div>
            <div className='text-sm text-gray-500'>
              Showing {applications.length} of {allApplications.length} applications
            </div>
          </div>
        </div>
        <div className='flex flex-col gap-2 p-4 border-t'>
          {applications.length === 0 ? (
            <div className='text-center py-12'>
              <p className='text-neutralColor-light mb-6'>
                {statusFilter === 'all'
                  ? 'No nominee applications have been submitted for this lottery yet.'
                  : `No applications found with status "${statusFilter === 'not-selected' ? 'Not Selected' : statusFilter === 'selected' ? 'Selected' : statusFilter}".`}
              </p>
            </div>
          ) : (
            <div className='mt-4 w-full overflow-x-scroll overflow-y-scroll max-h-[500px]'>
              <table className='w-full'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='p-6'></th>
                    {columns.map((col) => (
                      <th key={col.id} className='p-6 text-left text-xs font-bold text-gray-500 capitalize tracking-wider'>
                        {typeof col.header === 'string' ? col.header : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-gray-200'>
                  {applications.map((row) => (
                    <React.Fragment key={row.id}>
                      <tr className='hover:bg-green-50 cursor-pointer' key={`${row.id}-main`}>
                        <td className='px-6 py-4'>
                          <button
                            aria-label={expandedRow === row.id ? 'Collapse details' : 'Expand details'}
                            onClick={() => setExpandedRow(expandedRow === row.id ? null : row.id)}
                            className='focus:outline-hidden'
                          >
                            {expandedRow === row.id ? <ChevronDownIcon className='w-5 h-5' /> : <ChevronRightIcon className='w-5 h-5' />}
                          </button>
                        </td>
                        {columns.map((col) => {
                          const accessorKey = (col as any).accessorKey as string;
                          const value = accessorKey ? getValueByPath(row, accessorKey) : row[col.id as keyof NomineeApplication];
                          return (
                            <td key={col.id} className='px-6 py-4 whitespace-nowrap'>
                              {typeof value === 'object' || typeof value === 'undefined' ? '-' : value}
                            </td>
                          );
                        })}
                      </tr>
                      {expandedRow === row.id && (
                        <tr key={`${row.id}-expanded`}>
                          <td colSpan={columns.length + 1} className='bg-gray-50 px-6 py-4'>
                            <div className='text-sm'>
                              <div className='font-semibold mb-2'>Additional Info</div>
                              <div className='mb-1'>
                                <span className='font-medium'>Secondary Email:</span> {row.additionalInfo?.secondaryInfo?.email || '-'}
                              </div>
                              <div>
                                <span className='font-medium'>Secondary Phone Number:</span>{' '}
                                {row.additionalInfo?.secondaryInfo?.contactInfo || '-'}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </LayoutCard>
    </Layout>
  );
};

export default NomineeApplications;
